/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { fmtDate } from "../lib/format";
import SortableGrid from "../components/SortableGrid";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { TableIcon, Trash, ArrowsClockwise, FileText, PencilSimple } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const wt = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const SYM = { USD: "US $", INR: "\u20B9", EUR: "\u20AC", GBP: "\u00A3" };
const money = (n, c) => `${SYM[(c || "USD").toUpperCase()] || ""} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const chip = (on) => ({ padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 999, border: "1px solid var(--jde-border)", background: on ? "var(--omh-primary,#2A5C9B)" : "white", color: on ? "white" : "#475569" });

// Cache the last fetched list + scroll position so returning to this screen (e.g. via the
// browser back button after opening a plan in the editor) restores the exact list and
// scroll position instead of re-fetching and jumping to the top. Module-level so it
// survives unmount. Mirrors the Orders (MsPOs) behaviour.
const planListCache = { data: null, scrollY: 0 };

export default function DispatchPlans() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");     // all | mine
  const [status, setStatus] = useState("all");    // all | draft | posted
  const [invoice, setInvoice] = useState("all");   // all | invoiced | not
  const [cust, setCust] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/dispatch/plan")
      .then((r) => { setPlans(r.data || []); planListCache.data = r.data || []; })
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
  };

  // On mount: hydrate from cache + restore scroll (no refetch flash) if we've been here
  // before; otherwise fetch fresh.
  useEffect(() => {
    if (planListCache.data) {
      setPlans(planListCache.data);
      setLoading(false);
      const y = planListCache.scrollY || 0;
      if (y > 0) {
        let tries = 0;
        const restore = () => {
          window.scrollTo(0, y);
          if (Math.abs(window.scrollY - y) > 2 && tries++ < 40) setTimeout(restore, 40);
        };
        requestAnimationFrame(restore);
      }
    } else {
      load();
    }
  }, []);

  // Remember scroll position when leaving so back-navigation lands where we left.
  useEffect(() => {
    const save = () => { planListCache.scrollY = window.scrollY; };
    window.addEventListener("scroll", save, { passive: true });
    return () => { save(); window.removeEventListener("scroll", save); };
  }, []);

  const customers = useMemo(() => [...new Set(plans.map((p) => p.customer_name).filter(Boolean))].sort(), [plans]);

  const rows = useMemo(() => plans.filter((p) => {
    if (scope === "mine" && p.created_by !== user?.username) return false;
    if (status !== "all" && (p.status || "draft") !== status) return false;
    if (invoice === "invoiced" && !p.invoiced) return false;
    if (invoice === "not" && p.invoiced) return false;
    if (cust && p.customer_name !== cust) return false;
    return true;
  }), [plans, scope, status, invoice, cust, user]);

  const canDelete = (p) => (user?.role === "admin" || p.created_by === user?.username) &&
    (p.status !== "posted" || p.invoiced === false);

  const del = async (p) => {
    const msg = p.status === "posted"
      ? `Delete dispatch Voc# ${p.voc_no} from the system?\n\n${p.customer_name || p.pcode} \u00b7 ${fmt(p.tot_qty)} EA \u00b7 ${money(p.tot_amount, p.currency)}\n\nThis removes the dispatch from the system and cannot be undone.`
      : "Delete this draft plan?";
    if (!window.confirm(msg)) return;
    try { await api.delete(`/dispatch/plan/${p.id}`); toast.success(p.status === "posted" ? "Dispatch deleted from system" : "Draft deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const openProforma = async (p) => {
    try {
      const res = await api.get(`/dispatch/plan/${p.id}/proforma.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not generate proforma invoice"); }
  };

  const repost = async (p) => {
    try {
      const d = (await api.post(`/dispatch/plan/${p.id}/repost?confirm=false`)).data;
      if (!window.confirm(`Re-post dispatch to MS SQL portal?\n\nThis REWRITES the existing Voc# ${d.voc_no} — the old lines are removed and replaced with the edited plan.\n\n${p.customer_name || p.pcode} \u00b7 Plant ${p.plant}\n${d.line_count} line(s) \u00b7 ${fmt(d.tot_qty)} units \u00b7 ${money(d.tot_amount, p.currency)}\n\nRefused if an invoice already exists for this dispatch.`)) return;
      const res = (await api.post(`/dispatch/plan/${p.id}/repost?confirm=true`)).data;
      toast.success(`Re-posted \u00b7 Voc_No ${res.voc_no}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Re-post failed"); }
  };

  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  const columns = [
    { key: "created_at", header: "Created", type: "date", accessor: (p) => p.created_at,
      render: (p) => <span className="text-mono" style={{ fontSize: 11 }}>{fmtDate(p.created_at)}</span> },
    { key: "customer_name", header: "Customer",
      render: (p) => <span style={{ fontWeight: 700 }}>{p.customer_name || p.pcode}{p.transfer_price && <span className="jde-badge" style={{ marginLeft: 6, fontSize: 8, background: "#CCFBF1", color: "#0F766E", fontWeight: 700 }}>TP</span>}</span> },
    { key: "plant", header: "Plant" },
    { key: "tot_qty", header: "Qty", align: "right", accessor: (p) => Number(p.tot_qty) || 0,
      render: (p) => <span className="text-mono">{fmt(p.tot_qty)}</span> },
    { key: "tot_amount", header: "Value", align: "right", accessor: (p) => Number(p.tot_amount) || 0,
      render: (p) => <span className="text-mono">{money(p.tot_amount, p.currency)}</span> },
    { key: "status", header: "Status", accessor: (p) => p.status || "draft",
      render: (p) => <span className="jde-badge" style={{ fontSize: 10, fontWeight: 700, background: p.status === "posted" ? "#DCFCE7" : "#F1F5F9", color: p.status === "posted" ? "#166534" : "#475569" }}>{(p.status || "draft").toUpperCase()}</span> },
    { key: "voc_no", header: "Voc#", accessor: (p) => p.voc_no || "",
      render: (p) => <span className="text-mono">{p.voc_no || "—"}</span> },
    { key: "invoice", header: "Invoice", sortable: false,
      render: (p) => p.status !== "posted" ? <span className="muted">—</span>
        : p.invoiced ? <span style={{ color: "#166534", fontWeight: 700 }} data-testid={`dpl-invstat-${p.id}`}>#{p.inv_no}{p.inv_date ? ` \u00b7 ${p.inv_date}` : ""}</span>
          : p.invoiced === false ? <span style={{ color: "#B45309", fontWeight: 700 }} data-testid={`dpl-invstat-${p.id}`}>Not invoiced</span>
            : <span className="muted" data-testid={`dpl-invstat-${p.id}`}>—</span> },
    { key: "created_by", header: "By", accessor: (p) => p.created_by || "",
      render: (p) => <span>{p.created_by || "—"}</span> },
    { key: "actions", header: "Actions", sortable: false,
      render: (p) => p.status !== "posted"
        ? <button className="jde-btn jde-btn-sm" onClick={stop(() => nav(`/dispatch-plan?edit=${p.id}`))} data-testid={`dpl-edit-${p.id}`} title="Edit this draft plan" style={{ color: "#B45309" }}><PencilSimple size={12} weight="bold" /> Edit</button>
        : p.invoiced === true
          ? <span className="muted" style={{ fontSize: 10 }} title="Invoiced — read-only">read-only</span>
          : <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <button className="jde-btn jde-btn-sm" onClick={stop(() => nav(`/dispatch-plan?edit=${p.id}`))} data-testid={`dpl-edit-${p.id}`} title="Edit this posted plan (re-post to update MS SQL)" style={{ color: "#B45309" }}><PencilSimple size={12} weight="bold" /> Edit</button>
              <button className="jde-btn jde-btn-sm" onClick={stop(() => repost(p))} data-testid={`dpl-repost-${p.id}`} title="Rewrite this voucher in MS SQL with the current plan" style={{ color: p.needs_repost ? "#B45309" : "#0F766E", fontWeight: 700 }}><ArrowsClockwise size={12} weight="bold" /> Re-post</button>
            </div> },
    { key: "proforma", header: "", sortable: false,
      render: (p) => <button className="jde-btn jde-btn-sm" onClick={stop(() => openProforma(p))} data-testid={`dpl-proforma-${p.id}`} title="Proforma Invoice (PDF)"><FileText size={12} weight="bold" /> Proforma</button> },
    { key: "del", header: "", sortable: false,
      render: (p) => canDelete(p)
        ? <button className="jde-btn jde-btn-sm" onClick={stop(() => del(p))} data-testid={`dpl-del-${p.id}`} style={{ color: "#B91C1C" }}><Trash size={12} weight="bold" /></button>
        : <span className="muted" style={{ fontSize: 10 }} title={p.invoiced ? "Invoiced — cannot delete" : "Not your plan"}>{p.invoiced ? "invoiced" : ""}</span> },
  ];

  const toolbarRight = (
    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 10.5, fontWeight: 700 }}>SCOPE:</span>
        <span style={chip(scope === "all")} onClick={() => setScope("all")} data-testid="dpl-scope-all">All</span>
        <span style={chip(scope === "mine")} onClick={() => setScope("mine")} data-testid="dpl-scope-mine">My plans</span>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 10.5, fontWeight: 700 }}>STATUS:</span>
        {[["all", "All"], ["draft", "Draft"], ["posted", "Posted"]].map(([k, l]) => (
          <span key={k} style={chip(status === k)} onClick={() => setStatus(k)} data-testid={`dpl-status-${k}`}>{l}</span>))}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className="muted" style={{ fontSize: 10.5, fontWeight: 700 }}>INVOICE:</span>
        {[["all", "All"], ["invoiced", "Invoiced"], ["not", "Not invoiced"]].map(([k, l]) => (
          <span key={k} style={chip(invoice === k)} onClick={() => setInvoice(k)} data-testid={`dpl-inv-${k}`}>{l}</span>))}
      </div>
      <select className="jde-select" value={cust} onChange={(e) => setCust(e.target.value)} data-testid="dpl-customer" style={{ minWidth: 170 }}>
        <option value="">All customers</option>
        {customers.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <button className="jde-btn" onClick={load} data-testid="dpl-refresh"><ArrowsClockwise size={13} weight="bold" /> Refresh</button>
    </div>
  );

  return (
    <Layout breadcrumb={["Dispatch Plans"]}>
      <div className="jde-page">
        <div className="jde-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><TableIcon size={14} weight="bold" style={{ verticalAlign: "-2px", marginRight: 6 }} /> DISPATCH PLANS ({rows.length}{loading ? "…" : ""})</div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <SortableGrid pageSize={100}
              columns={columns}
              rows={rows}
              getRowKey={(p) => p.id}
              renderExpanded={(p) => <PlanLinesDetail plan={p} nav={nav} openProforma={openProforma} />}
              defaultSort={{ key: "created_at", dir: "desc" }}
              searchPlaceholder="Quick filter loaded plans…"
              testid="dpl-table"
              toolbarRight={toolbarRight}
              emptyMessage="No dispatch plans match these filters."
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function PlanLinesDetail({ plan, nav, openProforma }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/dispatch/plan/${plan.id}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [plan.id]);

  const th = { textAlign: "left", padding: "6px 10px", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#64748B", borderBottom: "1px solid var(--jde-border)", whiteSpace: "nowrap" };
  const td = { padding: "6px 10px", fontSize: 12.5, borderBottom: "1px solid #EEF1F4" };
  const lines = detail?.lines || [];
  const cur = detail?.currency || plan.currency || "USD";
  const editable = plan.status !== "posted" || plan.invoiced !== true;

  return (
    <div style={{ padding: "10px 16px 14px" }} data-testid={`dpl-lines-${plan.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#475569" }}>
          Line items · {plan.customer_name || plan.pcode} <span className="muted" style={{ fontWeight: 500 }}>({lines.length})</span>
        </span>
        {detail?.consignee_name && <span className="muted" style={{ fontSize: 11 }}>Ship-to: {detail.consignee_name}</span>}
        {plan.voc_no && <span className="jde-badge" style={{ fontSize: 9, background: "#DCFCE7", color: "#166534", fontWeight: 700 }}>Voc# {plan.voc_no}</span>}
        <span style={{ flex: 1 }} />
        <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); openProforma(plan); }} data-testid={`dpl-detail-proforma-${plan.id}`}><FileText size={12} weight="bold" /> Proforma</button>
        {editable && <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); nav(`/dispatch-plan?edit=${plan.id}`); }} data-testid={`dpl-detail-edit-${plan.id}`} style={{ color: "#B45309" }}><PencilSimple size={12} weight="bold" /> {plan.status === "posted" ? "Edit / re-post →" : "Edit in planner →"}</button>}
      </div>

      {loading && <div className="muted" style={{ padding: 10, fontSize: 12 }}>Loading line items…</div>}
      {error && <div style={{ padding: 10, fontSize: 12, color: "#B91C1C" }}>Could not load line items.</div>}

      {!loading && !error && (
        <div style={{ overflowX: "auto", border: "1px solid var(--jde-border)", borderRadius: 6, background: "white" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {[["Item", "left"], ["Description", "left"], ["PO", "left"], ["Rel", "left"], ["Deliv", "left"], ["Bal Qty", "right"], ["Disp Qty", "right"], ["Rate", "right"], ["Amount", "right"], ["Net / Gross", "right"]].map(([h, a]) => (
                <th key={h} style={{ ...th, textAlign: a }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={`${l.icode}-${idx}`}>
                  <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.icode || "—"}</td>
                  <td style={td}>{l.iname || "—"}</td>
                  <td style={td} className="text-mono">{l.custpo || l.po || "—"}</td>
                  <td style={td}><span className="jde-badge confirmed" style={{ fontSize: 9 }}>{l.release || "—"}</span></td>
                  <td style={td} className="text-mono">{fmtDate(l.deliv_date)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmt(l.bal_qty)}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#0F766E" }} className="text-mono">{fmt(l.disp_qty)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{l.rate}</td>
                  <td style={{ ...td, textAlign: "right" }} className="text-mono">{money((Number(l.disp_qty) || 0) * (Number(l.rate) || 0), l.currency || cur)}</td>
                  <td className="text-mono" style={{ ...td, textAlign: "right", fontSize: 11 }}>{wt(l.net_wt)} / {wt(l.gross_wt)}</td>
                </tr>))}
              {lines.length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 14, textAlign: "center" }}>No line items.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
